import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import api from '../../api/auth';

const ParentPayments = () => {
  const { childId } = useParams();
  const [child, setChild] = useState(null);
  const [payments, setPayments] = useState([]);
  const [totalPaid, setTotalPaid] = useState(0);
  const [totalPending, setTotalPending] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (childId) {
      fetchPayments(childId);
    } else {
      fetchFirstChild();
    }
  }, [childId]);

  const fetchFirstChild = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await api.get('/parent/children', {
        headers: { Authorization: `Bearer ${token}` }
      });
      let childrenList = response.data;
      if (response.data && response.data.data) childrenList = response.data.data;
      if (Array.isArray(childrenList) && childrenList.length > 0) {
        fetchPayments(childrenList[0].id);
      } else {
        setLoading(false);
      }
    } catch (error) {
      console.error('Erreur:', error);
      setLoading(false);
    }
  };

  const fetchPayments = async (id) => {
    try {
      const token = localStorage.getItem('token');
      const response = await api.get(`/parent/children/${id}/payments`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setChild(response.data.child);
      setPayments(response.data.payments || []);
      setTotalPaid(parseFloat(response.data.total_paid || 0));
      setTotalPending(parseFloat(response.data.total_pending || 0));
    } catch (error) {
      console.error('Erreur:', error);
      setPayments([]);
    } finally {
      setLoading(false);
    }
  };

  const formatAmount = (amount) => {
    return new Intl.NumberFormat('fr-FR').format(amount) + ' FCFA';
  };

  const getStatusColor = (status) => {
    if (status === 'paid') return 'bg-green-100 text-green-700';
    if (status === 'pending') return 'bg-yellow-100 text-yellow-700';
    if (status === 'failed') return 'bg-red-100 text-red-700';
    return 'bg-gray-100 text-gray-700';
  };

  const getStatusLabel = (status) => {
    if (status === 'paid') return '✅ Payé';
    if (status === 'pending') return '⏳ En attente';
    if (status === 'failed') return '❌ Échoué';
    if (status === 'refunded') return '↩️ Remboursé';
    return status;
  };

  if (loading) {
    return <div className="text-center py-8">Chargement...</div>;
  }

  const total = totalPaid + totalPending;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-[#2C3E50]">💰 Paiements</h1>
        <p className="text-gray-500">
          {child?.name || 'Élève'} - {child?.class_name || ''}
        </p>
      </div>

      {/* Résumé financier */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white rounded-xl shadow-md p-6 border-l-4 border-blue-500">
          <p className="text-sm text-gray-500 mb-1">Total payé</p>
          <p className="text-2xl font-bold text-blue-600">
            {formatAmount(totalPaid)}
          </p>
        </div>
        <div className="bg-white rounded-xl shadow-md p-6 border-l-4 border-orange-500">
          <p className="text-sm text-gray-500 mb-1">Reste à payer</p>
          <p className="text-2xl font-bold text-orange-600">
            {formatAmount(totalPending)}
          </p>
        </div>
        <div className="bg-white rounded-xl shadow-md p-6 border-l-4 border-gray-700">
          <p className="text-sm text-gray-500 mb-1">Total</p>
          <p className="text-2xl font-bold text-gray-800">
            {formatAmount(total)}
          </p>
        </div>
      </div>

      {/* Bouton payer */}
      {totalPending > 0 && (
        <div className="bg-gradient-to-r from-[#E67E22] to-[#D35400] rounded-xl shadow-md p-6 text-white">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-bold">💳 Payer maintenant</h3>
              <p className="text-sm opacity-90">
                Reste à payer : {formatAmount(totalPending)}
              </p>
            </div>
            <button className="bg-white text-[#E67E22] px-6 py-3 rounded-lg font-semibold hover:bg-gray-100 transition">
              Payer
            </button>
          </div>
        </div>
      )}

      {/* Historique */}
      <div className="bg-white rounded-xl shadow-md overflow-hidden">
        <div className="p-4 border-b border-gray-200">
          <h3 className="font-bold text-[#2C3E50]">Historique des paiements</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">Référence</th>
                <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">Montant</th>
                <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">Date</th>
                <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">Méthode</th>
                <th className="text-left py-3 px-4 text-sm font-semibold text-gray-600">Statut</th>
              </tr>
            </thead>
            <tbody>
              {payments.length === 0 ? (
                <tr>
                  <td colSpan="5" className="text-center py-8 text-gray-500">
                    Aucun paiement enregistré
                  </td>
                </tr>
              ) : (
                payments.map((payment) => (
                  <tr key={payment.id} className="border-t hover:bg-gray-50">
                    <td className="py-3 px-4 font-mono text-sm">{payment.reference}</td>
                    <td className="py-3 px-4 font-bold">{formatAmount(payment.amount)}</td>
                    <td className="py-3 px-4 text-sm">{payment.date || '-'}</td>
                    <td className="py-3 px-4 text-sm capitalize">{payment.method || '-'}</td>
                    <td className="py-3 px-4">
                      <span className={`px-2 py-1 rounded-full text-xs ${getStatusColor(payment.status)}`}>
                        {getStatusLabel(payment.status)}
                      </span>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default ParentPayments;